// @ts-nocheck
/*
 * Port of views/dashboard/ivr/IVR/nodes/DefaultNode.js (387 lines).
 * BYTE-IDENTICAL — reactflow → @xyflow/react, AudioPreviewModel → AudioPreviewModal.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Handle, useReactFlow } from '@xyflow/react';
import { Button, Form } from './_rbShim';
import { NodesContext } from '../provider/IvrContext';
import AudioPreviewModal from '../Helpers/AudioPreviewModal';
import { toast } from 'sonner';
import { SlidersHorizontal, Settings, Play, ArrowRight } from 'lucide-react';
import IvrDrawer from '../Helpers/IvrDrawer';
import { triggerHandleConnect, findUpstreamLanguageNode } from '../Helpers/utils';
import {
  BACKEND_API_BASE_URL,
  VOICEMAIL_MAX_DURATION,
  VOICEMAIL_MIN_DURATION,
  TRANSFER_DEFAULT_OPTIONS,
} from '@/utilities/constant';
import { getApiCall } from '@/service/httpCall';

const DefaultNode = ({ data, id }: any) => {
  const { updateNodeData } = useContext(NodesContext) as any;
  const { getNodes, getEdges } = useReactFlow();

  const mode = data.mode || '';
  const action = data.action || {};
  const maxDuration = data.max_duration || '';

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [playModalOpen, setPlayModalOpen] = useState(false);
  const [promptList, setPromptList] = useState<any[]>([]);
  const [errors, setErrors] = useState<any>({});

  const [answerLangCodes, setAnswerLangCodes] = useState<any[]>([]);
  const [hasLangParent, setHasLangParent] = useState(false);
  const [draftMode, setDraftMode] = useState(mode);
  const [draftAction, setDraftAction] = useState<any>(action);
  const [draftLanguage, setDraftLanguage] = useState(action.language || '');
  const [draftCategory, setDraftCategory] = useState('general');
  const [draftDuration, setDraftDuration] = useState(maxDuration);
  const [draftUseMedia, setDraftUseMedia] = useState(Boolean(data.use_media));
  const [drawerPreview, setDrawerPreview] = useState<any>({
    open: false,
    url: '',
    name: '',
  });

  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        const resp: any = await getApiCall(
          `${BACKEND_API_BASE_URL}/v2/prompt?user_id=${data.userId}`,
        );
        if (!Array.isArray(resp)) return;
        setPromptList(
          resp.filter(
            (p: any) =>
              p.prompt_category === 'general' &&
              Array.isArray(p.prompt_url) &&
              p.prompt_url.some((e: any) => e.status === 1),
          ),
        );
      } catch (err) {
        console.error(err);
      }
    };
    if (data.userId) fetchPrompts();
  }, [data.userId]);

  const updateNode = (updates: any) => updateNodeData(id, updates);

  const openModal = () => {
    const _answerCodes =
      getNodes().find((n: any) => n.type === 'answerNode')?.data?.languages ||
      [];
    const _langNode = findUpstreamLanguageNode(id, getNodes, getEdges);
    const _langCodes =
      _langNode &&
      Array.isArray(_langNode.data?.selectedLanguages) &&
      _langNode.data.selectedLanguages.length > 0
        ? _langNode.data.selectedLanguages
        : null;
    setHasLangParent(!!_langNode);
    setAnswerLangCodes(_langCodes ?? _answerCodes);
    setDraftMode(mode);
    setDraftAction(action);
    setDraftLanguage(action.language || '');
    setDraftCategory('general');
    setDraftDuration(maxDuration);
    setDraftUseMedia(Boolean(data.use_media));
    setErrors({});
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setErrors({});
    setModalIsOpen(false);
  };

  const handleModeChange = (e: any) => {
    const m = e.target.value;
    setDraftMode(m);
    setDraftAction({});
    setDraftCategory('');
    setDraftDuration('');
    setDraftUseMedia(false);
  };

  const handleUseMediaToggle = (e: any) => {
    const checked = e.target.checked;
    setDraftUseMedia(checked);
    if (!checked) {
      setDraftCategory('');
      setDraftAction({});
    }
  };

  const handleAudioChange = (e: any) => {
    const selected = promptList.find(
      (a: any) => a.id.toString() === e.target.value,
    );
    if (!selected) {
      setDraftAction((prev: any) => ({
        ...prev,
        id: '',
        audio: '',
        name: '',
        duration: 0,
      }));
      setDraftLanguage('');
      return;
    }
    const approvedLangs = Array.isArray(selected.prompt_url)
      ? selected.prompt_url.filter(
          (en: any) =>
            en.status === 1 &&
            (answerLangCodes.length === 0 ||
              answerLangCodes.includes(en.language)),
        )
      : [];

    if (hasLangParent) {
      // Downstream of a Language node — keep every approved language variant, no manual pick.
      setDraftAction({
        type: draftMode,
        prompt_category: 'general',
        id: selected.id,
        name: selected.prompt_name,
        duration: selected.prompt_duration || 0,
        languages: approvedLangs
          .filter((en: any) => en.status === 1)
          .map((en: any) => ({
            language: en.language,
            url: en.url,
            duration: en.duration ?? selected.prompt_duration ?? 0,
            status: en.status,
          })),
      });
      setDraftLanguage('');
      return;
    }

    const autoLang = approvedLangs.length === 1 ? approvedLangs[0].language : '';
    const autoAudio = approvedLangs.length === 1 ? approvedLangs[0].url : '';
    setDraftAction({
      type: draftMode,
      prompt_category: 'general',
      id: selected.id,
      audio: autoAudio,
      duration: selected.prompt_duration || 0,
      name: selected.prompt_name,
      language: autoLang,
    });
    setDraftLanguage(autoLang);
  };

  const handleLanguageChange = (e: any) => {
    const lang = e.target.value;
    setDraftLanguage(lang);
    const sel = promptList.find(
      (a: any) => a.id?.toString() === draftAction?.id?.toString(),
    );
    const entry =
      lang && sel && Array.isArray(sel.prompt_url)
        ? sel.prompt_url.find((en: any) => en.language === lang)
        : null;
    setDraftAction((prev: any) => ({
      ...prev,
      audio: entry?.url || '',
      language: lang,
    }));
  };

  const handleDurationChange = (e: any) => {
    const val = e.target.value;
    if (
      /^\d*$/.test(val) &&
      (val === '' || Number(val) <= VOICEMAIL_MAX_DURATION)
    ) {
      setDraftDuration(val);
    }
  };

  const validate = () => {
    const newErrors: any = {};
    if (!draftMode) newErrors.mode = 'Action is required';
    if (draftMode === 'hangup_with_media') {
      if (draftCategory && !draftAction?.id) {
        newErrors.audio = 'Audio is required when category is selected';
      }
      if (!hasLangParent && draftAction?.id && !draftLanguage)
        newErrors.language = 'Please select a language';
    }
    if (draftMode === 'voicemail') {
      if (draftUseMedia && draftCategory && !draftAction?.id) {
        newErrors.audio = 'Audio is required when category is selected';
      }
      if (
        !hasLangParent &&
        draftUseMedia &&
        draftAction?.id &&
        !draftLanguage
      )
        newErrors.language = 'Please select a language';
      if (!draftDuration || Number(draftDuration) < VOICEMAIL_MIN_DURATION) {
        newErrors.duration = `Min ${VOICEMAIL_MIN_DURATION}s`;
      } else if (Number(draftDuration) > VOICEMAIL_MAX_DURATION) {
        newErrors.duration = `Max ${VOICEMAIL_MAX_DURATION}s`;
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const updates: any = { mode: draftMode };
    if (draftMode === 'hangup') {
      updates.action = {};
      updates.max_duration = null;
      updates.use_media = false;
    } else if (draftMode === 'voicemail') {
      updates.action = draftUseMedia ? draftAction : {};
      updates.max_duration = Number(draftDuration);
      updates.use_media = draftUseMedia;
    } else {
      updates.action = draftAction;
      updates.max_duration = null;
      updates.use_media = false;
    }
    updateNode(updates);
    toast.success('Default action updated.');
    setModalIsOpen(false);
  };

  const displayPromptList =
    answerLangCodes.length === 0
      ? promptList
      : promptList.filter(
          (p: any) =>
            Array.isArray(p.prompt_url) &&
            p.prompt_url.some(
              (e: any) =>
                e.status === 1 && answerLangCodes.includes(e.language),
            ),
        );
  const audioUrl = action?.audio || action?.languages?.[0]?.url || '';
  const audioName = action?.name || '';

  const renderLangControl = () => {
    const sel = promptList.find(
      (a: any) => a.id?.toString() === draftAction?.id?.toString(),
    );
    if (!sel) return null;
    if (hasLangParent) {
      const langs = draftAction?.languages;
      if (!Array.isArray(langs) || langs.length === 0) return null;
      return (
        <div
          style={{
            marginTop: 10,
            fontSize: 12,
            color: '#0f766e',
            background: '#f0fdfa',
            border: '1px solid #99f6e4',
            borderRadius: 8,
            padding: '8px 12px',
          }}
        >
          This node is downstream of a Language node — all {langs.length}{' '}
          approved language{langs.length !== 1 ? 's' : ''} (
          {langs.map((l: any) => l.language?.toUpperCase()).join(', ')}) will
          be saved automatically.
        </div>
      );
    }
    const langs = Array.isArray(sel?.prompt_url)
      ? sel.prompt_url.filter(
          (e: any) =>
            e.status === 1 &&
            (answerLangCodes.length === 0 ||
              answerLangCodes.includes(e.language)),
        )
      : [];
    if (langs.length === 0) return null;
    return (
      <Form.Group style={{ marginTop: 10 }}>
        <Form.Label>
          Select Audio Language <span style={{ color: 'red' }}>*</span>
        </Form.Label>
        <Form.Control
          as="select"
          value={draftLanguage}
          onChange={handleLanguageChange}
        >
          <option value="">Select Language</option>
          {langs.map((e: any) => (
            <option key={e.language} value={e.language}>
              {e.language?.toUpperCase()}
              {e.name ? ` — ${e.name}` : ''}
            </option>
          ))}
        </Form.Control>
        {errors.language && (
          <Form.Text className="text-danger">{errors.language}</Form.Text>
        )}
      </Form.Group>
    );
  };
  const modeLabel =
    TRANSFER_DEFAULT_OPTIONS.find((m: any) => m.value === mode)?.label ||
    'Default';

  const renderListenBtn = (url: any, name: any) =>
    url ? (
      <button
        type="button"
        className="ivr-drawer-play-btn"
        title="Listen"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() =>
          setDrawerPreview({ open: true, url, name: name || 'Audio' })
        }
        style={{ marginLeft: 8 }}
      >
        ▶
      </button>
    ) : null;

  return (
    <div className="ivrn" style={{ '--node-color': '#475569' } as any}>
      <span
        className="ivrn-icon"
        style={{ background: '#475569', color: '#fff' }}
        onClick={triggerHandleConnect}
      >
        <SlidersHorizontal size={22} strokeWidth={2} />
      </span>
      <Handle
        type="target"
        position={'left' as any}
        id="default-target"
        style={{ background: '#475569', top: '65%' }}
        isConnectable={false}
      />

      <div className="ivrn-head">
        <span className="ivrn-title">Default Action</span>
      </div>
      <div className="ivrn-body">
        {mode ? (
          <span className="ivrn-val">
            <ArrowRight size={10} style={{ marginRight: 4, flexShrink: 0 }} />
            {modeLabel}
          </span>
        ) : (
          <span className="ivrn-hint">Handles uncovered time slots</span>
        )}
      </div>

      <div className="ivrn-actions">
        <button
          className="ivrn-btn ivrn-btn--cfg"
          title="Configure"
          onClick={openModal}
        >
          <Settings size={14} strokeWidth={2} />
        </button>
        {audioUrl && (
          <button
            className="ivrn-btn ivrn-btn--play"
            title="Preview"
            onClick={() => setPlayModalOpen(true)}
          >
            <Play size={14} strokeWidth={2} />
          </button>
        )}
      </div>

      <IvrDrawer
        show={modalIsOpen}
        onHide={closeModal}
        title="Default Action"
        subtitle="Configure action for time slots not covered by transfer rules."
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>
              Close
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </>
        }
      >
        <Form>
          <Form.Group>
            <Form.Label>
              Action<Form.Text className="text-danger">*</Form.Text>
            </Form.Label>
            <Form.Control
              as="select"
              value={draftMode}
              onChange={handleModeChange}
            >
              <option value="">Select Action</option>
              {TRANSFER_DEFAULT_OPTIONS.map((m: any) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Form.Control>
            {errors.mode && (
              <Form.Text className="text-danger">{errors.mode}</Form.Text>
            )}
          </Form.Group>

          {draftMode === 'hangup_with_media' && (
            <>
              <Form.Group style={{ marginTop: 15 }}>
                <Form.Label>Select Audio</Form.Label>
                <Form.Control
                  as="select"
                  value={draftAction?.id || ''}
                  onChange={handleAudioChange}
                >
                  <option value="">Select Audio</option>
                  {displayPromptList.map((item: any) => (
                    <option key={item.id} value={item.id}>
                      {item.prompt_name}
                    </option>
                  ))}
                </Form.Control>
                {errors.audio && (
                  <Form.Text className="text-danger">
                    {errors.audio}
                  </Form.Text>
                )}
              </Form.Group>
              {renderLangControl()}
              {renderListenBtn(
                draftAction?.audio || draftAction?.languages?.[0]?.url || '',
                draftAction?.name,
              )}
            </>
          )}

          {draftMode === 'voicemail' && (
            <>
              <Form.Group style={{ marginTop: 15 }}>
                <Form.Label>
                  Max Voicemail Duration (sec, {VOICEMAIL_MIN_DURATION}-
                  {VOICEMAIL_MAX_DURATION})
                  <Form.Text className="text-danger">*</Form.Text>
                </Form.Label>
                <Form.Control
                  type="number"
                  min={VOICEMAIL_MIN_DURATION}
                  max={VOICEMAIL_MAX_DURATION}
                  value={draftDuration}
                  onChange={handleDurationChange}
                />
                {errors.duration && (
                  <Form.Text className="text-danger">
                    {errors.duration}
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group style={{ marginTop: 15 }}>
                <Form.Check
                  type="checkbox"
                  id={`def-use-media-${id}`}
                  label="Use Media"
                  checked={draftUseMedia}
                  onChange={handleUseMediaToggle}
                />
              </Form.Group>

              {draftUseMedia && (
                <>
                  <Form.Group style={{ marginTop: 15 }}>
                    <Form.Label>Select Audio</Form.Label>
                    <Form.Control
                      as="select"
                      value={draftAction?.id || ''}
                      onChange={handleAudioChange}
                    >
                      <option value="">Select Audio</option>
                      {displayPromptList.map((item: any) => (
                        <option key={item.id} value={item.id}>
                          {item.prompt_name}
                        </option>
                      ))}
                    </Form.Control>
                    {errors.audio && (
                      <Form.Text className="text-danger">
                        {errors.audio}
                      </Form.Text>
                    )}
                  </Form.Group>
                  {renderLangControl()}
                  {renderListenBtn(
                    draftAction?.audio ||
                      draftAction?.languages?.[0]?.url ||
                      '',
                    draftAction?.name,
                  )}
                </>
              )}
            </>
          )}
        </Form>
      </IvrDrawer>

      <AudioPreviewModal
        show={playModalOpen}
        onClose={() => setPlayModalOpen(false)}
        audioUrl={audioUrl}
        title={audioName}
      />

      <AudioPreviewModal
        show={drawerPreview.open}
        onClose={() =>
          setDrawerPreview({ open: false, url: '', name: '' })
        }
        audioUrl={drawerPreview.url}
        title={drawerPreview.name}
      />
    </div>
  );
};

export default DefaultNode;
